/**
 * ThemeToggle — cycles light / dark / system and persists the choice.
 *
 * Writes the same `md-theme` key the pre-paint bootstrap in index.html reads, so
 * there is no flash on reload. "system" removes the attribute so tokens.css
 * follows prefers-color-scheme. Both themes are first-class (design rule).
 */
import { useCallback, useSyncExternalStore } from "react";
import { Button } from "./Button";

type Theme = "light" | "dark" | "system";

/** Read the persisted choice (or "system" when unset). */
function readTheme(): Theme {
  const attr = document.documentElement.getAttribute("data-theme");
  if (attr === "light" || attr === "dark") return attr;
  return "system";
}

/** Apply + persist a theme choice. */
function applyTheme(t: Theme): void {
  const root = document.documentElement;
  if (t === "system") {
    root.removeAttribute("data-theme");
    try {
      localStorage.removeItem("md-theme");
      // Mark that the user EXPLICITLY chose follow-OS, so the dark-first bootstrap
      // (index.html) does not re-apply the dark default on the next load.
      localStorage.setItem("md-theme-system", "1");
    } catch {
      /* ignore */
    }
  } else {
    root.setAttribute("data-theme", t);
    try {
      localStorage.setItem("md-theme", t);
      localStorage.removeItem("md-theme-system");
    } catch {
      /* ignore */
    }
  }
  // Notify subscribers (useSyncExternalStore below).
  window.dispatchEvent(new Event("md-theme-change"));
}

const NEXT: Record<Theme, Theme> = { system: "light", light: "dark", dark: "system" };
const LABEL: Record<Theme, string> = { system: "Auto", light: "Light", dark: "Dark" };

export function ThemeToggle() {
  const theme = useSyncExternalStore(
    (cb) => {
      window.addEventListener("md-theme-change", cb);
      return () => window.removeEventListener("md-theme-change", cb);
    },
    readTheme,
    () => "system" as Theme,
  );

  const cycle = useCallback(() => applyTheme(NEXT[theme]), [theme]);

  return (
    <Button variant="ghost" onClick={cycle} aria-label={`Theme: ${LABEL[theme]}. Activate to change.`}>
      {LABEL[theme]}
    </Button>
  );
}
